import { BCMSDate } from "@bcm-dev/bcms-vite";
import Classes, { OutgoingFedWire, IncomingFedWire } from "model";
/**
 * @class SearchForm
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {STRING} _receivingAccountID - The receivingAccountID to filter by
 * @property {STRING} _dateRange - The date range to filter by
 * @property {BCMSDate} _fromDate - The fromDate to filter by
 * @property {BCMSDate} _toDate - The toDate to filter by
 * @property {STRING} _wireNumber - The wireNumber to filter by
 * @property {ARRAY} _itemsToFilter - An array of items to be filtered
 * @property {OBJECT} _defaultValues - The default values for the search fields
 * 
 * @description - A class to manage a search field.
 */
export default class SearchForm {
    _itemsToFilter;
    _receivingAccountID;
    _dateRange;
    _fromDate;
    _toDate;
    _wireNumber;
    _maximumAmount;
    _minimumAmount;
    _defaultValues;

    constructor(record) {
        this.defaultValues = { 
            itemsToFilter: IncomingFedWire.initAll(),
            receivingAccountID: "all accounts",
            dateRange: "custom",
            fromDate: BCMSDate.init().offset(-7),
            toDate: undefined,
            wireNumber: undefined,
            maximumAmount: undefined,
            minimumAmount: undefined
        };

        this.itemsToFilter = record.itemsToFilter ?? this.defaultValues.itemsToFilter;
        this.receivingAccountID = record.receivingAccountID ?? this.defaultValues.receivingAccountID;
        this.dateRange = record.dateRange ?? this.defaultValues.dateRange;
        this.fromDate = record.fromDate ?? this.defaultValues.fromDate;
        this.toDate = record.toDate ?? this.defaultValues.toDate;
        this.wireNumber = record.wireNumber ?? this.defaultValues.wireNumber;
        this.maximumAmount = record.maximumAmount ?? this.defaultValues.maximumAmount;
        this.minimumAmount = record.minimumAmount ?? this.defaultValues.minimumAmount;
    }

    /**
     * @name itemsToFilter
     * @instance
     * @property set {ARRAY} - sets the array of items to filter.
     * @memberof SearchForm 
     */
    set itemsToFilter(value) { this._itemsToFilter = value; }

    /**
     * @name receivingAccountID
     * @instance
     * @property set {STRING} - sets the receivingAccountID.
     * @memberof SearchForm 
     */
    set receivingAccountID(value) { this._receivingAccountID = value; }

    /**
     * @name dateRange
     * @instance
     * @property set {STRING} - sets the dateRange.
     * @memberof SearchForm 
     */
    set dateRange(value) { this._dateRange = value; }

    /**
     * @name fromDate
     * @instance
     * @property set {STRING} - sets the fromDate.
     * @memberof SearchForm 
     */
    set fromDate(value) { this._fromDate = value; }

    /**
     * @name toDate
     * @instance
     * @property set {STRING} - sets the toDate.
     * @memberof SearchForm 
     */
    set toDate(value) { this._toDate = value; }

    /**
     * @name wireNumber
     * @instance
     * @property set {STRING} - sets the wireNumber.
     * @memberof SearchForm 
     */
    set wireNumber(value) { this._wireNumber = value; }

    /**
     * @name maximumAmount
     * @instance
     * @property set {BCMSCurrency} - sets the maximumAmount.
     * @memberof SearchForm 
     */
    set maximumAmount(value) { this._maximumAmount = value; }

    /**
     * @name minimumAmount
     * @instance
     * @property set {BCMSCurrency} - sets the minimumAmount.
     * @memberof SearchForm 
     */
    set minimumAmount(value) { this._minimumAmount = value; }

    /**
     * @name defaultValues
     * @instance
     * @property set {OBJECT} - sets the defaultValues.
     * @memberof SearchForm 
     */
    set defaultValues(value) { this._defaultValues = value; }

    /**
     * @name itemsToFilter
     * @instance
     * @property get {ARRAY} - returns the array of items to filter.
     * @memberof SearchForm
     */
    get itemsToFilter() { return this._itemsToFilter; }

    /**
     * @name receivingAccountID
     * @instance
     * @property get {STRING} - returns the receivingAccountID.
     * @memberof SearchForm
     */
    get receivingAccountID() { return this._receivingAccountID; }

    /**
     * @name dateRange
     * @instance
     * @property get {STRING} - returns the date range.
     * @memberof SearchForm
     */
    get dateRange() { return this._dateRange; }

    /**
     * @name fromDate
     * @instance
     * @property get {BCMSDate|STRING} - returns the starting date.
     * @memberof SearchForm
     */
    get fromDate() { return this._fromDate; }

    /**
     * @name toDate
     * @instance
     * @property get {BCMSDate|STRING} - returns the ending date.
     * @memberof SearchForm
     */
    get toDate() { return this._toDate; }

    /**
     * @name wireNumber
     * @instance
     * @property get {STRING} - returns the wire number.
     * @memberof SearchForm
     */
    get wireNumber() { return this._wireNumber; }

    /**
     * @name maximumAmount
     * @instance
     * @property get {BCMSCurrency} - returns the maximum amount.
     * @memberof SearchForm
     */
    get maximumAmount() { return this._maximumAmount; }

    /**
     * @name minimumAmount
     * @instance
     * @property get {BCMSCurrency} - returns the minimum amount.
     * @memberof SearchForm
     */
    get minimumAmount() { return this._minimumAmount; }

    /**
     * @name defaultValues
     * @instance
     * @property get {OBJECT} - returns the default values.
     * @memberof SearchForm
     */
    get defaultValues() { return this._defaultValues; }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof WireSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    get filters() {
        const res = Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && key !== "itemsToFilter") {
                result[key] = value;
            }

            return result;
        }, {});

        return res;
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof WireSearch
     */
    get record() {
        return {
            itemsToFilter: this.itemsToFilter,
            receivingAccountID: this.receivingAccountID,
            dateRange: this.dateRange,
            fromDate: this.fromDate,
            toDate: this.toDate,
            wireNumber: this.wireNumber,
            maximumAmount: this.maximumAmount,
            minimumAmount: this.minimumAmount
        };
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    checkValue(item, key) {
        switch(key) {
            case "receivingAccountID":
                return this.receivingAccountID === "all accounts" || item.receivingAccount.id === this.receivingAccountID;
            case "fromDate":
                return item.wireDate.isEqual(this.fromDate) || item.wireDate.isAfter(this.fromDate);
            case "toDate":
                return item.wireDate.isEqual(this.toDate) || item.wireDate.isBefore(this.toDate);
            case "wireNumber":
                return this.wireNumber === "" || item.wireNumber === this.wireNumber;
            default:
                return true;
        }
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    filter() {
        return this.itemsToFilter
            .filter(item => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(item, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }

    reset() {
        return SearchForm.init(this.defaultValues);
    }
    
    static init(record) {
        return new SearchForm(record ?? {});
    }
}